import { ComponentPropsWithoutRef, forwardRef, ReactNode, Ref } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { clsx } from '@unbridaled/shared/utils/clsx'
import { ModalHeader } from './ModalTitle'
import { ModalFooter } from './ModalFooter'
import { ModalContent } from './ModalContent'

export const ModalTrigger = DialogPrimitive.Trigger
export const ModalClose = DialogPrimitive.Close
export const ModalPortal = (props: ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>): React.ReactNode => (
  <DialogPrimitive.Portal {...props} data-slot="portal" />
)
export const ModalOverlay = ({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>): React.ReactNode => (
  <DialogPrimitive.Overlay
    {...props}
    data-slot="overlay"
    className={clsx(
      'uui-fixed uui-inset-0 uui-bg-black/30 uui-z-overlay',
      'data-[state=open]:uui-animate-fade-in data-[state=closed]:uui-animate-fade-out',
      className
    )}
  />
)

export type ModalRootProps = {
  open: boolean
  children?: ReactNode
  onClose?: () => void
  onOpenChange?: (open: boolean) => void
}
export const ModalRoot = ({ open, children, onClose, onOpenChange }: ModalRootProps): React.ReactNode => (
  <DialogPrimitive.Root
    open={open}
    onOpenChange={(open: boolean): void => {
      if (onOpenChange) {
        onOpenChange(open)
      } else if (onClose && !open) {
        onClose()
      }
    }}
  >
    {children}
  </DialogPrimitive.Root>
)

export type ModalPanelProps = {
  id?: string
  children?: ReactNode
  className?: string
  size?: string
  hideOverlay?: boolean
  ref?: Ref<HTMLDivElement>
  overlayProps?: ComponentPropsWithoutRef<typeof ModalOverlay>
  container?: HTMLElement
}
export const ModalPanel = forwardRef<HTMLDivElement, ModalPanelProps>(
  (
    {
      id,
      children,
      className,
      size = 'uui-w-full uui-max-w-[calc(100%-2rem)] sm:uui-max-w-xl md:uui-max-w-2xl lg:uui-max-w-3xl xl:uui-max-w-4xl',
      hideOverlay,
      overlayProps,
      container,
      ...props
    }: ModalPanelProps,
    forwardedRef: Ref<HTMLDivElement>
  ) => (
    <ModalPortal container={container}>
      <div id={id ? `uui-${id}-root` : 'uui-modal-root'}>
        {!hideOverlay && <ModalOverlay {...overlayProps} />}
        <div className="uui-fixed uui-inset-0 uui-z-overlay">
          <div className="uui-flex uui-justify-center uui-items-center uui-h-full">
            <DialogPrimitive.Content
              id={id || 'uui-modal-content'}
              ref={forwardedRef}
              className={clsx(
                'uui-rounded-md uui-shadow-xl uui-bg-white uui-overflow-auto uui-max-h-[90vh]',
                'data-[state=open]:uui-animate-in data-[state=closed]:uui-animate-out',
                size,
                className
              )}
              {...props}
            >
              {children}
            </DialogPrimitive.Content>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
)

export type ModalProps = ModalPanelProps &
  ModalRootProps & {
    trigger?: ReactNode
  }

export const Modal = ({ open, onClose, children, onOpenChange, trigger, ...props }: ModalProps): React.ReactNode => (
  <ModalRoot
    open={open}
    {...(onClose !== undefined ? { onClose } : {})}
    {...(onOpenChange !== undefined ? { onOpenChange } : {})}
  >
    {trigger}
    <ModalPanel {...props}>{children}</ModalPanel>
  </ModalRoot>
)

/**
 * @deprecated use ModalHeader
 */
Modal.Title = ModalHeader
/**
 * @deprecated use ModalFooter
 */
Modal.Footer = ModalFooter
/**
 * @deprecated use ModalContent
 */
Modal.Content = ModalContent

export function ModalDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>): React.ReactNode {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={clsx('text-sm text-tertiary', className)}
      {...props}
    />
  )
}
